import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Star, Send, CheckCircle, AlertCircle } from "lucide-react";
import { supportFeedbackService } from "@core/services/SupportFeedbackService";
import { useAuth } from "@core/contexts/AuthContext";

// color-exempt-file — retokenisation Phase 6 ui-themes : etoiles jaunes (yellow-400) laissees en l'etat, convention universelle de notation independante du theme

interface FeedbackCollectorProps {
  ticketId: string;
  ticketSubject: string;
  alreadySubmitted?: boolean;
}

export const FeedbackCollector: React.FC<FeedbackCollectorProps> = ({
  ticketId,
  ticketSubject,
  alreadySubmitted = false,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [hoveredRating, setHoveredRating] = useState(0);
  const [selectedRating, setSelectedRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(alreadySubmitted);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ratingLabels: Record<number, string> = {
    1: t("feedback_rating_1"),
    2: t("feedback_rating_2"),
    3: t("feedback_rating_3"),
    4: t("feedback_rating_4"),
    5: t("feedback_rating_5"),
  };

  const activeRating = hoveredRating || selectedRating;

  const handleSubmit = async () => {
    if (!selectedRating || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await supportFeedbackService.createFeedback(
        ticketId,
        selectedRating,
        comment || undefined,
        user?.id,
      );
      setSubmitted(true);
    } catch (err) {
      setError(t("errors:try_again"));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
        <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mb-6">
          <CheckCircle className="w-8 h-8 text-success" />
        </div>
        <h2 className="text-2xl font-semibold text-foreground mb-3">
          {t("feedback_thanks_title")}
        </h2>
        <p className="text-muted-foreground max-w-sm">
          {t("feedback_thanks_subtitle")}
        </p>
        {selectedRating > 0 && (
          <div className="mt-6 flex gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <Star
                key={s}
                className={`w-6 h-6 ${s <= selectedRating ? "text-yellow-400 fill-yellow-400" : "text-muted-foreground/40"}`}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg mx-auto">
      {/* Ticket subject */}
      <div className="bg-muted/50 border border-border rounded-xl p-4 mb-8">
        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
          {t("feedback_ticket_resolved")}
        </p>
        <p className="text-foreground font-medium">{ticketSubject}</p>
      </div>

      {/* Question */}
      <h2 className="text-xl font-semibold text-foreground mb-2 text-center">
        {t("feedback_question")}
      </h2>
      <p className="text-muted-foreground text-sm text-center mb-8">
        {t("feedback_question_subtitle")}
      </p>

      {/* Stars */}
      <div className="flex justify-center gap-3 mb-3">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => setSelectedRating(star)}
            onMouseEnter={() => setHoveredRating(star)}
            onMouseLeave={() => setHoveredRating(0)}
            className="transition-transform hover:scale-110 focus:outline-none"
            aria-label={t("feedback_star_aria", { count: star })}
          >
            <Star
              className={`w-10 h-10 transition-colors ${
                star <= activeRating
                  ? "text-yellow-400 fill-yellow-400"
                  : "text-muted-foreground/40"
              }`}
            />
          </button>
        ))}
      </div>

      {/* Label */}
      <div className="text-center h-6 mb-6">
        {activeRating > 0 && (
          <span className="text-sm text-muted-foreground font-medium">
            {ratingLabels[activeRating]}
          </span>
        )}
      </div>

      {/* Comment */}
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={t("feedback_comment_placeholder")}
        rows={3}
        className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-foreground placeholder:text-muted-foreground text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 mb-4"
      />

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-destructive text-sm mb-4">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Submit */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!selectedRating || submitting}
        className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-primary-foreground bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
      >
        {submitting ? (
          <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
        ) : (
          <Send className="w-4 h-4" />
        )}
        {submitting ? t("feedback_submitting") : t("feedback_submit")}
      </button>
    </div>
  );
};
